// repleeno-admin-fe/src/api.js

// ---- Base URL (from Vite) ----
export const API_BASE = (import.meta.env?.VITE_API_URL || "http://localhost:4000").replace(/\/+$/, "");

// ---- Helpers ----
const DEFAULT_TIMEOUT_MS = 20_000;

function buildHeaders(token) {
    const h = { "Content-Type": "application/json" };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
}

function toQuery(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        q.append(k, String(v));
    });
    const s = q.toString();
    return s ? `?${s}` : "";
}

async function parseResponse(res) {
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) return res.json();
    if (res.status === 204) return null; // no content
    // fallback: blob/arrayBuffer if non-JSON (e.g., QR image proxied in the future)
    return res.arrayBuffer();
}

async function request(path, { method = "GET", body, token, timeout = DEFAULT_TIMEOUT_MS } = {}) {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);

    try {
        const res = await fetch(`${API_BASE}${path}`, {
            method,
            headers: buildHeaders(token),
            body: body !== undefined ? JSON.stringify(body) : undefined,
            signal: controller.signal,
        });

        if (!res.ok) {
            // Try to extract meaningful error
            let message = `${res.status} ${res.statusText}`;
            try {
                const data = await res.clone().json();
                message = data?.error || data?.message || message;
            } catch {
                try {
                    message = await res.clone().text();
                } catch {/* ignore */ }
            }
            const err = new Error(message || "Request failed");
            err.status = res.status;
            err.statusText = res.statusText;
            throw err;
        }

        return parseResponse(res);
    } finally {
        clearTimeout(id);
    }
}

/* -------------------- AUTH -------------------- */
export const AuthAPI = {
    login: (email, password) =>
        request("/api/auth/login", { method: "POST", body: { email, password } }),

    me: (token) =>
        request("/api/auth/me", { token }),

    logout: (token) =>
        request("/api/auth/logout", { method: "POST", token }),
};

/* -------------------- MERCHANTS -------------------- */
export const MerchantsAPI = {
    list: (token) =>
        request("/api/merchants", { token }),

    create: (token, data) =>
        request("/api/merchants", { method: "POST", body: data, token }),

    patch: (token, id, patch) =>
        request(`/api/merchants/${id}`, { method: "PATCH", body: patch, token }),

    remove: (token, id) =>
        request(`/api/merchants/${id}`, { method: "DELETE", token }),
};

/* -------------------- RECEIVERS -------------------- */
export const ReceiversAPI = {
    list: (token) =>
        request("/api/receivers", { token }),

    create: (token, data) =>
        request("/api/receivers", { method: "POST", body: data, token }),

    patch: (token, id, patch) =>
        request(`/api/receivers/${id}`, { method: "PATCH", body: patch, token }),

    remove: (token, id) =>
        request(`/api/receivers/${id}`, { method: "DELETE", token }),
};

/* -------------------- DONATIONS -------------------- */
export const DonationsAPI = {
    list: (token) =>
        request("/api/donations", { token }),

    create: (token, data) =>
        request("/api/donations", { method: "POST", body: data, token }),

    assignNearest: (token, id) =>
        request(`/api/donations/${id}/assign-nearest`, { method: "POST", token }),
};

/* -------------------- DONOR DONATIONS -------------------- */
export const DonorDonationsAPI = {
    list: (token) =>
        request("/api/donor-donations", { token }),
};

/* -------------------- MENU -------------------- */
export const MenuAPI = {
    items: (token) =>
        request("/api/menu/items", { token }),

    addItem: (token, data) =>
        request("/api/menu/items", { method: "POST", body: data, token }),

    patchItem: (token, id, patch) =>
        request(`/api/menu/items/${id}`, { method: "PATCH", body: patch, token }),

    removeItem: (token, id) =>
        request(`/api/menu/items/${id}`, { method: "DELETE", token }),

    addons: (token) =>
        request("/api/menu/addons", { token }),

    addAddon: (token, data) =>
        request("/api/menu/addons", { method: "POST", body: data, token }),

    patchAddon: (token, id, patch) =>
        request(`/api/menu/addons/${id}`, { method: "PATCH", body: patch, token }),

    removeAddon: (token, id) =>
        request(`/api/menu/addons/${id}`, { method: "DELETE", token }),

    getCommission: (token) =>
        request("/api/menu/commission", { token }),

    setCommission: (token, commissionPercent) =>
        request("/api/menu/commission", { method: "PATCH", body: { commissionPercent }, token }),
};

/* -------------------- SETTINGS -------------------- */
export const SettingsAPI = {
    getBusiness: (token) =>
        request("/api/settings/business", { token }),

    setBusiness: (token, data) =>
        request("/api/settings/business", { method: "PATCH", body: data, token }),

    getPoster: (token) =>
        request("/api/settings/poster", { token }),

    setPoster: (token, data) =>
        request("/api/settings/poster", { method: "PATCH", body: data, token }),

    changePassword: (token, currentPassword, newPassword) =>
        request("/api/auth/change-password", { method: "POST", body: { currentPassword, newPassword }, token }),
};

/* -------------------- PAYMENTS -------------------- */
export const PaymentsAPI = {
    getPaymentSettings: (token) =>
        request("/api/settings/payments", { token }),

    setPaymentSettings: (token, data) =>
        request("/api/settings/payments", { method: "PATCH", body: data, token }),
};

/* -------------------- SEARCH -------------------- */
export const SearchAPI = {
    // server expects /api/search?q=...
    query: (token, q) =>
        request(`/api/search${toQuery({ q })}`, { token }),
};

/* -------------------- COUNTRIES -------------------- */
export const CountriesAPI = {
    list: (token) =>
        request("/api/countries", { token }),

    create: (token, data) =>
        request("/api/countries", { method: "POST", body: data, token }),

    patch: (token, id, patch) =>
        request(`/api/countries/${id}`, { method: "PATCH", body: patch, token }),

    remove: (token, id) =>
        request(`/api/countries/${id}`, { method: "DELETE", token }),
};

/* -------------------- QR -------------------- */
export const QRAPI = {
    // This returns a direct image URL served by backend
    url: (data, size = 300) =>
        `${API_BASE}/api/qr${toQuery({ data, size })}`,
};

/* -------------------- S3 UPLOADS -------------------- */
export const S3UploadsAPI = {
    uploadImage: (token, file) => {
        const formData = new FormData();
        formData.append('image', file);
        return fetch(`${API_BASE}/api/s3-uploads/image`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        }).then(res => {
            if (!res.ok) {
                throw new Error(`Upload failed: ${res.status}`);
            }
            return res.json();
        });
    },

    uploadImages: (token, files) => {
        const formData = new FormData();
        files.forEach(file => {
            formData.append('images', file);
        });
        return fetch(`${API_BASE}/api/s3-uploads/images`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        }).then(res => {
            if (!res.ok) {
                throw new Error(`Upload failed: ${res.status}`);
            }
            return res.json();
        });
    },

    deleteImage: (token, key) =>
        request(`/api/s3-uploads/image/${encodeURIComponent(key)}`, { method: "DELETE", token }),
};
